import moment from 'moment'

/**
 * Генератор РЕАЛИСТИЧНЫХ демо-данных по операциям CTRL+C.
 *
 * Прайс CTRL+C: печать — 12 ₽/лист, копир — 13 ₽/лист.
 * Чтобы суммы «бились» под цену за лист, большинство чеков = N × 12 или N × 13
 * (напр. 348 = 29×12, 377 = 29×13). Часть чеков — круглые, кратные 5/10/15 ₽.
 *
 * Модель как в генераторе «Велочки»:
 *  - введённая сумма — это МЕСЯЧНАЯ норма на аппарат;
 *  - дневная плотность = норма / 30;
 *  - «Сгенерировать» строит историю за выбранный период N дней (норма × N/30);
 *  - «Нагенерить за сегодня» — догенерирует операции за сегодняшний день по той же
 *    дневной норме, НЕ стирая историю (append, даты = сегодня, время ≤ текущего момента);
 *  - «Очистить данные» — сбрасывает localStorage в дефолт.
 *
 * Всё персистится в localStorage: при навигации внутри демо суммы НЕ перегенерируются.
 */

export const PRICE_PRINT = 12
export const PRICE_COPY = 13

export const DEMO_DEVICE_COUNT = 24
const STORAGE_KEY = 'CTRLC_DEMO_DATA_V1'
/** Дефолтная месячная норма на один аппарат, если генератор ещё ни разу не запускали. */
const DEFAULT_MONTHLY_PER_DEVICE = 50000
/** Период по умолчанию, дней. */
export const DEFAULT_DAYS = 30
/** Пресеты периода для UI. */
export const DAYS_PRESETS = [7, 30, 90, 180]

// ---------- базовые утилиты ----------

const randInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min

/** N листов: «от пары до нескольких десятков», со смещением в сторону меньших значений. */
const randomSheets = () => Math.round(2 + Math.pow(Math.random(), 1.7) * 38) // 2..40

/** Компактная запись чека в хранилище (без предрендеренного HTML — он строится при чтении). */
interface StoredCheck {
    s: number // сумма, ₽
    t: string // тип операции
    w: string // способ оплаты
    d: string // дата 'DD.MM.YYYY HH:mm:ss'
}

export interface DemoData {
    settings: {
        monthlyPerDevice: number
        days: number
        generatedAt: string
        periodBeg: string // 'YYYY-MM-DD'
        periodEnd: string // 'YYYY-MM-DD'
        deviceCount: number
        lastAppendAt?: string
    }
    devices: Record<number, StoredCheck[]>
}

/**
 * Один правдоподобный чек. ПРАВИЛА СУММ НЕ МЕНЯТЬ:
 * ~72% — привязаны к цене за лист (N×12 печать / N×13 копир),
 * ~28% — круглые суммы, кратные 5/10/15 ₽ (сканирование).
 */
const makeCheck = (): { sum: number; type: string } => {
    const r = Math.random()
    if (r < 0.72) {
        const isCopy = Math.random() < 0.45
        const price = isCopy ? PRICE_COPY : PRICE_PRINT
        const sheets = randomSheets()
        return { sum: sheets * price, type: isCopy ? 'Копирование' : 'Печать' }
    }
    const step = [5, 10, 15][randInt(0, 2)]
    const base = randInt(60, 540)
    const sum = Math.max(step, Math.round(base / step) * step)
    return { sum, type: 'Сканирование' }
}

const randomWayPay = () => (Math.random() > 0.25 ? 'СБП' : 'Терминал')

type TimeFn = (day: moment.Moment) => string

/** Время по умолчанию: случайный момент в рабочих часах дня (8:00–21:59). */
const dayTimeFn: TimeFn = (day) =>
    day.clone().hour(randInt(8, 21)).minute(randInt(0, 59)).second(randInt(0, 59)).format('DD.MM.YYYY HH:mm:ss')

/**
 * Накапливает чеки за ОДИН день до дневной цели.
 * Остановка по БЛИЖАЙШЕМУ к цели — дневная сумма центрируется вокруг цели (без
 * систематического перебора), поэтому итог честно ложится в ±~7%.
 */
const accumulateDay = (checks: StoredCheck[], day: moment.Moment, dayTarget: number, timeFn: TimeFn) => {
    if (dayTarget < 20) return
    let daySum = 0
    let guard = 0
    while (guard < 400) {
        guard += 1
        const { sum, type } = makeCheck()
        if (daySum + sum > dayTarget) {
            const over = daySum + sum - dayTarget
            const under = dayTarget - daySum
            if (over < under) {
                checks.push({ s: sum, t: type, w: randomWayPay(), d: timeFn(day) })
                daySum += sum
            }
            break
        }
        checks.push({ s: sum, t: type, w: randomWayPay(), d: timeFn(day) })
        daySum += sum
    }
}

const sortDesc = (checks: StoredCheck[]) =>
    checks.sort(
        (a, b) =>
            moment(b.d, 'DD.MM.YYYY HH:mm:ss').valueOf() - moment(a.d, 'DD.MM.YYYY HH:mm:ss').valueOf()
    )

/**
 * Операции одного аппарата за период: распределяем ОБЩУЮ сумму периода по дням
 * (с дневным шумом) и внутри дня — на чеки. Итог ±~7% от заданной суммы периода.
 */
const buildDeviceChecks = (periodTotal: number, begISO: string, endISO: string): StoredCheck[] => {
    const start = moment(begISO, 'YYYY-MM-DD').startOf('day')
    const end = moment(endISO, 'YYYY-MM-DD').startOf('day')

    const days: moment.Moment[] = []
    const cursor = start.clone()
    while (cursor.isSameOrBefore(end, 'day')) {
        days.push(cursor.clone())
        cursor.add(1, 'day')
    }
    if (days.length === 0) return []

    const effectiveTotal = periodTotal * (1 + (Math.random() * 2 - 1) * 0.07)

    const weights = days.map(() => 0.6 + Math.random() * 0.8)
    const weightsSum = weights.reduce((a, b) => a + b, 0)

    const checks: StoredCheck[] = []
    days.forEach((day, idx) => {
        const dayTarget = (effectiveTotal * weights[idx]) / weightsSum
        accumulateDay(checks, day, dayTarget, dayTimeFn)
    })

    return sortDesc(checks)
}

/** Операции одного аппарата за СЕГОДНЯ (время ≤ текущего момента, не в будущем). */
const buildTodayChecks = (dailyNorm: number): StoredCheck[] => {
    const now = moment()
    const today = now.clone().startOf('day')
    // дневной шум ±10% вокруг дневной нормы
    const dayTarget = dailyNorm * (0.9 + Math.random() * 0.2)

    const workStart = today.clone().add(8, 'hour')
    const earliest = now.isSameOrAfter(workStart) ? workStart : today.clone()
    const spanMs = Math.max(1, now.diff(earliest))
    const todayTimeFn: TimeFn = () =>
        earliest.clone().add(Math.floor(Math.random() * spanMs), 'ms').format('DD.MM.YYYY HH:mm:ss')

    const checks: StoredCheck[] = []
    accumulateDay(checks, today, dayTarget, todayTimeFn)
    return checks
}

// ---------- persistence ----------

const readStorage = (): DemoData | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (!parsed?.devices || !parsed?.settings) return null
        return parsed as DemoData
    } catch {
        return null
    }
}

const writeStorage = (data: DemoData) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    } catch {
        /* quota / приватный режим — демо просто отработает в памяти текущей сессии */
    }
}

/** Границы периода: последние `days` дней, заканчивая вчера (история без сегодня). */
export const getPeriod = (days: number) => ({
    beg: moment().subtract(days, 'day').format('YYYY-MM-DD'),
    end: moment().subtract(1, 'day').format('YYYY-MM-DD'),
})

export interface GenerateResult {
    monthlyPerDevice: number
    days: number
    dailyNorm: number
    deviceCount: number
    totalChecks: number
    networkTotal: number
    avgPerDevice: number
    periodBeg: string
    periodEnd: string
}

export interface AppendResult {
    dailyNorm: number
    deviceCount: number
    addedChecks: number
    addedSum: number
    date: string
}

/**
 * Полная перегенерация истории по всем аппаратам за период `days` и сохранение.
 * Введённая сумма — МЕСЯЧНАЯ норма; дневная плотность = норма/30; итог периода = норма×days/30.
 * Вызывается по кнопке «Сгенерировать».
 */
export const generateDemoData = (monthlyPerDevice: number, days: number = DEFAULT_DAYS): GenerateResult => {
    const norm = Math.max(0, Math.round(monthlyPerDevice) || 0)
    const dayCount = Math.max(1, Math.round(days) || DEFAULT_DAYS)
    const { beg, end } = getPeriod(dayCount)
    const periodTotalPerDevice = (norm * dayCount) / 30

    const devices: Record<number, StoredCheck[]> = {}
    let totalChecks = 0
    let networkTotal = 0

    for (let id = 1; id <= DEMO_DEVICE_COUNT; id += 1) {
        const checks = buildDeviceChecks(periodTotalPerDevice, beg, end)
        devices[id] = checks
        totalChecks += checks.length
        networkTotal += checks.reduce((acc, c) => acc + c.s, 0)
    }

    const data: DemoData = {
        settings: {
            monthlyPerDevice: norm,
            days: dayCount,
            generatedAt: moment().toISOString(),
            periodBeg: beg,
            periodEnd: end,
            deviceCount: DEMO_DEVICE_COUNT,
        },
        devices,
    }
    writeStorage(data)

    return {
        monthlyPerDevice: norm,
        days: dayCount,
        dailyNorm: Math.round(norm / 30),
        deviceCount: DEMO_DEVICE_COUNT,
        totalChecks,
        networkTotal,
        avgPerDevice: Math.round(networkTotal / DEMO_DEVICE_COUNT),
        periodBeg: beg,
        periodEnd: end,
    }
}

/**
 * Догенерировать операции за СЕГОДНЯ ко всем аппаратам по текущей дневной норме
 * (норма/30), НЕ стирая историю. Ритуал перед показом.
 */
export const appendTodayDemoData = (): AppendResult => {
    const data = ensureDemoData()
    const norm = data.settings.monthlyPerDevice || DEFAULT_MONTHLY_PER_DEVICE
    const dailyNorm = norm / 30

    let addedChecks = 0
    let addedSum = 0

    for (let id = 1; id <= DEMO_DEVICE_COUNT; id += 1) {
        const todays = buildTodayChecks(dailyNorm)
        addedChecks += todays.length
        addedSum += todays.reduce((acc, c) => acc + c.s, 0)
        data.devices[id] = sortDesc([...(data.devices[id] || []), ...todays])
    }

    data.settings.lastAppendAt = moment().toISOString()
    writeStorage(data)

    return {
        dailyNorm: Math.round(dailyNorm),
        deviceCount: DEMO_DEVICE_COUNT,
        addedChecks,
        addedSum,
        date: moment().format('DD.MM.YYYY'),
    }
}

/** Сброс localStorage в дефолт (чистая история за 30 дней по дефолтной норме). */
export const clearDemoData = (): GenerateResult => {
    try {
        localStorage.removeItem(STORAGE_KEY)
    } catch {
        /* ignore */
    }
    return generateDemoData(DEFAULT_MONTHLY_PER_DEVICE, DEFAULT_DAYS)
}

/**
 * Гарантирует наличие данных: если генератор ещё не запускали — создаёт дефолтный
 * набор ОДИН раз и персистит его (чтобы при навигации ничего не «скакало»).
 */
export const ensureDemoData = (): DemoData => {
    const existing = readStorage()
    if (existing) return existing
    generateDemoData(DEFAULT_MONTHLY_PER_DEVICE, DEFAULT_DAYS)
    return readStorage() as DemoData
}

export const getDemoSettings = () => ensureDemoData().settings

export interface DemoOperation {
    id: number
    orderId: string
    typeOperationName: string
    countOperation: string
    rawCountOperation: number
    errorName: string
    sumOperation: string
    rawSumOperation: number
    wayPayName: string
    statusPayName: string
    errorId: number
    jobs: any[]
    dateCreate: string
}

/** Операции конкретного аппарата в форме, которую ожидает таблица статистики. */
export const getDeviceOperations = (deviceId?: number | string | null): DemoOperation[] => {
    const data = ensureDemoData()
    const key = Number(deviceId)
    const checks = (Number.isFinite(key) && data.devices[key]) || []

    return checks.map((c, index) => ({
        id: index + 1,
        orderId: `demo-${key}-${index + 1}`,
        typeOperationName: c.t,
        countOperation: `<div style='margin-left: 80px'>1</div>`,
        rawCountOperation: 1,
        errorName: 'Выполнено',
        sumOperation: `<div style='margin-left: 55px'>${c.s} р.</div>`,
        rawSumOperation: c.s,
        wayPayName: c.w,
        statusPayName: 'Подтверждён',
        errorId: 0,
        jobs: [],
        dateCreate: c.d,
    }))
}
